/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useGetAvailableAppsQuery } from '@api/users';
import { ClickHandlers, HomepageProductCard, HomepageView } from '../types';
import { ProductAppType } from '@g17eco/types/app';
import { Loader } from '@g17eco/atoms/loader';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { emissionsTrackerAppCard } from '../configs/_common';
import config from '../../../../config';
import './MyAppsCards.scss';
import { ThemedImage } from '@features/theme/components/ThemedImage';

interface MyAppCard extends HomepageProductCard {
  isFree: boolean;
  isAlpha: boolean;
}

const Card = ({ card }: { card: MyAppCard }) => {
  return (
    <div className='my-apps__card' onClick={card.onClick}>
      <div className='my-apps__card-image'>
        <ThemedImage src={card.logo} alt={card.title} />
        {card.isFree ? <div className='my-apps__card-free text-xs text-ThemeSuccessDark'>FREE</div> : null}
        {card.isAlpha ? <div className='my-apps__card-alpha text-xs text-ThemeWarningMedium'>ALPHA</div> : null}
      </div>
    </div>
  );
};

interface MyAppsCardsProps {
  cards: HomepageProductCard[];
  clickHandlers: ClickHandlers;
}
export const MyAppsCards = (props: MyAppsCardsProps) => {
  const { cards, clickHandlers } = props;

  const { data: availableApps = [], isLoading } = useGetAvailableAppsQuery();
  const userIsStaff = useAppSelector(isStaff);

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center my-2'>
        <Loader relative />
      </div>
    );
  }

  const staticFreeApps = [ProductAppType.WorldTracker, ProductAppType.SDGAP];
  const allApps = [...availableApps, ...staticFreeApps];

  // Include ET card from API-driven availableApps if user has subscription
  const allCards = availableApps.includes(ProductAppType.EmissionsTracker)
    ? [...cards, emissionsTrackerAppCard({ clickHandlers, isLoggedIn: true, isUserStaff: userIsStaff, view: HomepageView.Europe })]
    : cards;

  const myAppCards = allCards.reduce((acc, card) => {
    if (card.id && allApps.includes(card.id)) {
      acc.push({
        ...card,
        isFree: staticFreeApps.includes(card.id),
        isAlpha: false,
      });
    }
    return acc;
  }, [] as MyAppCard[]);

  return (
    <section className='d-flex flex-wrap'>
      <h2 className='mt-6'>MY APPS</h2>
      <div className='my-apps__list w-100 flex-wrap justify-content-center mt-3'>
        {myAppCards.map((card) => (
          <Card key={card.id} card={card} />
        ))}
      </div>
      <div className='my-apps__divider background-ThemeBorderDefault mt-6' />
    </section>
  );
};
